/**
 * CIPAA Form Component
 *
 * Form for creating and editing CIPAA elections.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { CipaaFormData, Cipaa } from '../../../types/cipaa';
import { CompanySelectionModal, CompanyInfoBox } from '../../../components/forms';
import { useCompanySelection } from '../../../hooks/useCompanySelection';

interface CipaaFormProps {
  initialData?: Cipaa;
  onSubmit: (data: CipaaFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export const CipaaForm: React.FC<CipaaFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}) => {
  const isEditMode = !!initialData;
  const [formData, setFormData] = useState<CipaaFormData>({
    name: '',
    companyId: '',
    accessKey: '',
    startDate: '',
    startTime: '08:00',
    endDate: '',
    endTime: '17:00',
    electionDate: '',
    registrationOpenDate: '',
    candidateAnnouncementDate: '',
    resultsAnnouncementDate: '',
    termStartDate: '',
    termEndDate: '',
    registrationLocation: '',
    responsible: '',
    effectiveMembers: 0,
    alternateMembers: 0,
    employeeCount: undefined,
    riskLevel: undefined,
  });
  const {
    modalOpen,
    selectedCompany,
    showInfoBox,
    openModal,
    handleSelect: handleCompanySelect,
  } = useCompanySelection({ isEditMode, currentCompanyId: String(formData.companyId ?? '') });

  // Auto-fill company when selection changes
  useEffect(() => {
    if (selectedCompany && selectedCompany.id) {
      const currentValue = String(formData.companyId || '');
      if (currentValue !== selectedCompany.id) {
        setFormData((prev) => ({
          ...prev,
          companyId: Number(selectedCompany.id) || '',
        }));
      }
    }
  }, [selectedCompany, formData.companyId]);

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        companyId: initialData.companyId,
        accessKey: initialData.accessKey,
        startDate: initialData.startDate,
        startTime: initialData.startTime,
        endDate: initialData.endDate,
        endTime: initialData.endTime,
        electionDate: initialData.electionDate ?? '',
        registrationOpenDate: initialData.registrationOpenDate ?? '',
        candidateAnnouncementDate: initialData.candidateAnnouncementDate ?? '',
        resultsAnnouncementDate: initialData.resultsAnnouncementDate ?? '',
        termStartDate: initialData.termStartDate ?? '',
        termEndDate: initialData.termEndDate ?? '',
        registrationLocation: initialData.registrationLocation ?? '',
        responsible: initialData.responsible ?? '',
        effectiveMembers: initialData.effectiveMembers,
        alternateMembers: initialData.alternateMembers,
        employeeCount: initialData.employeeCount,
        riskLevel: initialData.riskLevel,
      });
    }
  }, [initialData]);

  const handleChange = (field: keyof CipaaFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleNumberChange = (field: keyof CipaaFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value === '' ? undefined : parseInt(event.target.value, 10);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {showInfoBox && selectedCompany && (
        <CompanyInfoBox
          companyName={selectedCompany.name}
          onEdit={openModal}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom>
            Informações Básicas
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            label="Nome da Eleição"
            value={formData.name}
            onChange={handleChange('name')}
            fullWidth
            required
            placeholder="Ex: CIPAA 2026"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            label="Chave de Acesso"
            value={formData.accessKey}
            onChange={handleChange('accessKey')}
            fullWidth
            placeholder="Deixe vazio para gerar automaticamente"
            helperText="Usada para acesso público à votação"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            label="Responsável"
            value={formData.responsible}
            onChange={handleChange('responsible')}
            fullWidth
          />
        </Grid>

        {/* Voting Period */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Período de Votação
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <TextField
            label="Data Início"
            type="date"
            value={formData.startDate}
            onChange={handleChange('startDate')}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <TextField
            label="Hora Início"
            type="time"
            value={formData.startTime}
            onChange={handleChange('startTime')}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <TextField
            label="Data Fim"
            type="date"
            value={formData.endDate}
            onChange={handleChange('endDate')}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <TextField
            label="Hora Fim"
            type="time"
            value={formData.endTime}
            onChange={handleChange('endTime')}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Important Dates */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Datas Importantes
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <TextField
            label="Abertura Inscrições"
            type="date"
            value={formData.registrationOpenDate}
            onChange={handleChange('registrationOpenDate')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <TextField
            label="Data da Eleição"
            type="date"
            value={formData.electionDate}
            onChange={handleChange('electionDate')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <TextField
            label="Divulgação Candidatos"
            type="date"
            value={formData.candidateAnnouncementDate}
            onChange={handleChange('candidateAnnouncementDate')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <TextField
            label="Divulgação Resultados"
            type="date"
            value={formData.resultsAnnouncementDate}
            onChange={handleChange('resultsAnnouncementDate')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <TextField
            label="Início do Mandato"
            type="date"
            value={formData.termStartDate}
            onChange={handleChange('termStartDate')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4
          }}>
          <TextField
            label="Fim do Mandato"
            type="date"
            value={formData.termEndDate}
            onChange={handleChange('termEndDate')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* CIPA Dimension */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Dimensionamento da CIPA
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <TextField
            label="Número de Funcionários"
            type="number"
            value={formData.employeeCount ?? ''}
            onChange={handleNumberChange('employeeCount')}
            fullWidth
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <FormControl fullWidth>
            <InputLabel>Grau de Risco</InputLabel>
            <Select
              value={formData.riskLevel ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  riskLevel: !e.target.value ? undefined : Number(e.target.value),
                }))
              }
              label="Grau de Risco"
            >
              <MenuItem value="">Selecione</MenuItem>
              <MenuItem value={1}>1 - Baixo</MenuItem>
              <MenuItem value={2}>2 - Médio</MenuItem>
              <MenuItem value={3}>3 - Alto</MenuItem>
              <MenuItem value={4}>4 - Muito Alto</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <TextField
            label="Titulares"
            type="number"
            value={formData.effectiveMembers}
            onChange={handleNumberChange('effectiveMembers')}
            fullWidth
            required
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <TextField
            label="Suplentes"
            type="number"
            value={formData.alternateMembers}
            onChange={handleNumberChange('alternateMembers')}
            fullWidth
            required
            inputProps={{ min: 0 }}
          />
        </Grid>

        {/* Registration Location */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Local de Inscrição
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid size={12}>
          <TextField
            label="Local para Inscrição de Candidaturas"
            value={formData.registrationLocation}
            onChange={handleChange('registrationLocation')}
            fullWidth
            multiline
            rows={3}
            placeholder="Endereço ou instruções para inscrição presencial"
          />
        </Grid>

        {/* Actions */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Salvando...' : initialData ? 'Atualizar' : 'Criar'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <CompanySelectionModal
        open={modalOpen}
        onSelect={handleCompanySelect}
      />
    </Box>
  );
};

export default CipaaForm;
